import { useEffect, useState } from "react";
import { recordFeatureHintView } from "./api";

type HintState = {
  loading: boolean;
  shouldShow: boolean;
};

/**
 * Shows a hint/banner for a user's first `maxViews` visits to whatever page
 * calls this, then stops automatically — the view count is tracked per user
 * on the backend, so it's consistent across devices and survives clearing
 * browser data, rather than resetting every time someone switches browsers.
 * Fails safe (shouldShow=false) if the request errors, so a backend hiccup
 * never blocks the page on an unwanted banner.
 */
export function useFeatureHint(featureKey: string, maxViews = 5): HintState {
  const [state, setState] = useState<HintState>({ loading: true, shouldShow: false });

  useEffect(() => {
    let active = true;
    setState({ loading: true, shouldShow: false });
    recordFeatureHintView(featureKey, maxViews)
      .then((result) => {
        if (active) setState({ loading: false, shouldShow: result.shouldShow });
      })
      .catch(() => {
        if (active) setState({ loading: false, shouldShow: false });
      });
    return () => {
      active = false;
    };
  }, [featureKey, maxViews]);

  return state;
}
